// Under every draft on a card (U5): Shorter · Warmer · Firmer · More formal as a diff the owner
// accepts or rejects, and, when dictation is on (Chrome only), a mic whose transcript becomes the
// owner's draft instruction "turn this into a reply in my voice: …".
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { DraftRequestStatus } from "@shared/api";
import { dictationInstruction } from "@shared/unstick";
import { DictateButton } from "@web/compose/DictateButton";
import { ToneControl } from "@web/compose/ToneControl";
import { isApiError } from "@web/lib/api";
import { acceptCard } from "@web/lib/feedQueries";
import { unstickApi } from "@web/lib/unstickApi";

const DICTATED_WORDS: Partial<Record<DraftRequestStatus, string>> = {
  queued: "Claude is turning what you said into a reply.",
  working: "Claude is turning what you said into a reply.",
  busy: "Claude is already working on this card. Try again once it's done.",
  no_model: "Claude isn't connected, so it can't write this one.",
  not_needed: "This card isn't waiting for a reply from you.",
};

/** Sends what the owner said as their draft instruction for the card. */
export async function dictateDraft(
  queryClient: QueryClient,
  cardId: string,
  words: string,
): Promise<void> {
  try {
    const res = await unstickApi.draftFromInstruction(cardId, dictationInstruction(words));
    acceptCard(queryClient, res.card);
    toast(DICTATED_WORDS[res.status] ?? "Sent to Claude.");
  } catch (error) {
    toast(
      isApiError(error, "not_available")
        ? "Claude isn't connected, so it can't write this one."
        : "Couldn't send that to Claude just now. Nothing changed.",
    );
  }
}

export function DraftTools({
  cardId,
  value,
  onChange,
  onSave,
  readOnly,
}: {
  cardId: string;
  value: string;
  onChange: (value: string) => void;
  /** Saves the draft (the card saves on blur; an accepted rewrite saves at once). */
  onSave: () => void;
  readOnly: boolean;
}) {
  const queryClient = useQueryClient();
  return (
    <ToneControl
      text={value}
      cardId={cardId}
      disabled={readOnly}
      onAccept={(text) => {
        onChange(text);
        // After the new text is in place (the next render), save it like an edit.
        setTimeout(onSave, 0);
      }}
      trailing={
        <DictateButton
          disabled={readOnly}
          onDone={(words) => void dictateDraft(queryClient, cardId, words)}
        />
      }
    />
  );
}
